import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from 'react';

import { isJwtExpired } from '@/lib/auth/jwt';
import {
  clearAllAuthStorage,
  permissionsStorage,
  refreshStorage,
  tokenStorage,
  userStorage,
} from '@/lib/auth/storage';
import { logoutRequest } from '@/services/auth';
import type { AuthState, AuthUser, LogoutOptions } from '@/types/auth';

const AuthContext = createContext<AuthState | undefined>(undefined);

/**
 * Initial-mount rehydration: read user + token from storage.
 *
 * "Remember me for 30 days" relies on a refresh token outliving the 15-minute
 * access token. If we wiped storage the moment the access token was expired,
 * the refresh token (and the 30-day session it represents) would be destroyed
 * before the axios interceptor ever got a chance to use it.
 *
 * Decision matrix on boot:
 *  - No stored user           → return null (no session, nothing to clear).
 *  - Access token still valid → return the user (normal warm boot).
 *  - Access token expired but refresh token present → keep storage intact and
 *    return the user. The first protected API request will hit the axios
 *    request interceptor, which silently calls /api/auth/refresh, replaces
 *    both tokens, and replays the request. The user never sees a login screen.
 *  - Access token expired AND no refresh token → clear and return null. There
 *    is no recovery path; treat the session as logged-out.
 */
function rehydrateInitialUser(): AuthUser | null {
  const stored = userStorage.read();
  if (stored === null) return null;
  const accessExpired = isJwtExpired(tokenStorage.read());
  if (!accessExpired) return stored;
  if (refreshStorage.read() !== null) {
    // Keep the session — axios will refresh on the first API call.
    return stored;
  }
  clearAllAuthStorage();
  return null;
}

function rehydrateInitialPermissions(initialUser: AuthUser | null): readonly string[] {
  // If rehydrateInitialUser wiped storage, mirror that here -- otherwise the
  // permissions memo would surface stale codes for a logged-out user.
  if (initialUser === null) return [];
  return permissionsStorage.read();
}

export function AuthProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(rehydrateInitialUser);
  const [permissions, setPermissions] = useState<readonly string[]>(() =>
    rehydrateInitialPermissions(user),
  );

  const login = useCallback(
    (
      token: string,
      refresh: string,
      nextUser: AuthUser,
      nextPermissions: readonly string[],
    ): void => {
      tokenStorage.write(token);
      refreshStorage.write(refresh);
      userStorage.write(nextUser);
      permissionsStorage.write(nextPermissions);
      setUser(nextUser);
      setPermissions(nextPermissions);
    },
    [],
  );

  const logout = useCallback(
    async (options: LogoutOptions = {}): Promise<void> => {
      const skipBackendCall = options.skipBackendCall === true;
      if (!skipBackendCall) {
        const refresh = refreshStorage.read();
        // Best-effort: never throws — see services/auth.ts logoutRequest.
        await logoutRequest(refresh);
      }
      clearAllAuthStorage();
      setUser(null);
      setPermissions([]);
      // REVIEW FF-020: drop the TanStack cache on logout so a subsequent
      // login (same tab, different user — shared kiosk) does not surface the
      // previous user's site / WO / KPI data until staleTime expires.
      queryClient.clear();
    },
    [queryClient],
  );

  /**
   * REVIEW FF-018: flip in-memory + persisted user.passwordChangeRequired to
   * false after a voluntary change-password succeeds. Without this the user
   * snapshot still flags the requirement and ForcePasswordChangeGuard
   * redirects right back to /force-change-password.
   */
  const markPasswordChanged = useCallback((): void => {
    setUser((prev) => {
      if (prev === null) return prev;
      if (prev.passwordChangeRequired === false) return prev;
      const next: AuthUser = { ...prev, passwordChangeRequired: false };
      userStorage.write(next);
      return next;
    });
  }, []);

  // Listen for the `auth:logout` event the axios interceptor dispatches on 401 / expiry.
  useEffect(() => {
    const onAuthLogout = (): void => {
      setUser(null);
      setPermissions([]);
      // REVIEW FF-020: same isolation as `logout()` above — wipe any cached
      // queries that belonged to the prior session.
      queryClient.clear();
    };
    window.addEventListener('auth:logout', onAuthLogout);
    return () => {
      window.removeEventListener('auth:logout', onAuthLogout);
    };
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      permissions,
      isAuthenticated: user !== null && tokenStorage.read() !== null,
      login,
      logout,
      markPasswordChanged,
    }),
    [user, permissions, login, logout, markPasswordChanged],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
}
